import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { detectLanguageFromPath, getLocalizedRoute, getBaseRoute } from '@/lib/routes';
import { getServiceIdFromSlug, getLocalizedServiceSlug } from '@/lib/serviceSlugMapping';
import { getInternalArticleSlug, getLocalizedArticleSlug } from '@/lib/articleSlugMapping';
import { translations } from './translations';

type Language = 'ro' | 'ru';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Detect language from URL first, then fallback to localStorage
  const [language, setLanguage] = useState<Language>(() => {
    // First, try to detect from URL (using window.location since we're in initial state)
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const urlLanguage = detectLanguageFromPath(currentPath);
      if (urlLanguage) {
        return urlLanguage;
      }
    }
    
    // Fallback to localStorage
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
    const savedLanguage = localStorage.getItem('byteet-language');
    // Migrate old language codes to new ones
    const validLanguages: Language[] = ['ro', 'ru'];
    if (savedLanguage && validLanguages.includes(savedLanguage as Language)) {
      return savedLanguage as Language;
    }
    // Migrate old language codes
    if (savedLanguage === 'en' || savedLanguage === 'nl') {
      const migratedLanguage = savedLanguage === 'en' ? 'ro' : 'ru';
      localStorage.setItem('byteet-language', migratedLanguage);
      return migratedLanguage;
    }
    return 'ro';
      }
    } catch (error) {
      console.warn('Failed to access localStorage:', error);
    }
    return 'ro';
  });
  
  // Sync language with URL when location changes
  useEffect(() => {
    const urlLanguage = detectLanguageFromPath(location.pathname);
    if (urlLanguage !== language) {
      setLanguage(urlLanguage);
      // Update localStorage to match URL
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('byteet-language', urlLanguage);
        }
      } catch (error) {
        console.warn('Failed to save language to localStorage:', error);
      }
    }
  }, [location.pathname, language]);

  const handleSetLanguage = (lang: Language) => {
    if (lang === language) return; // No change needed
    
    setLanguage(lang);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('byteet-language', lang);
      }
    } catch (error) {
      console.warn('Failed to save language to localStorage:', error);
    }
    
    // Navigate to the localized version of the current route
    const baseRoute = getBaseRoute(location.pathname);
    const newRoute = getLocalizedRoute(baseRoute, lang);
    
    // Handle dynamic routes (servicii/:id, articole/:slug for RO; uslugi/:id, stati/:slug for RU)
    let finalRoute = newRoute;
    if (location.pathname.includes('/servicii/') || location.pathname.includes('/uslugi/')) {
      const serviceSlugOrId = location.pathname.split('/servicii/')[1] || location.pathname.split('/uslugi/')[1];
      if (serviceSlugOrId) {
        // Get internal service ID from localized slug
        const serviceId = getServiceIdFromSlug(serviceSlugOrId) || serviceSlugOrId;
        // Get localized slug for target language
        const localizedSlug = getLocalizedServiceSlug(serviceId as any, lang);
        finalRoute = lang === 'ru' ? `/ru/uslugi/${localizedSlug}` : `/servicii/${localizedSlug}`;
      }
    } else if (location.pathname.includes('/articole/') || location.pathname.includes('/stati/')) {
      const articleSlugOrInternal = location.pathname.split('/articole/')[1] || location.pathname.split('/stati/')[1];
      if (articleSlugOrInternal) {
        // Get internal article slug from localized slug
        const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
        // Get localized slug for target language
        const localizedSlug = getLocalizedArticleSlug(internalSlug, lang);
        finalRoute = lang === 'ru' ? `/ru/stati/${localizedSlug}` : `/articole/${localizedSlug}`;
      }
    }
    
    navigate(finalRoute, { replace: true });
  };

  const t = (key: string): any => {
    if (!translations) {
      console.warn('Translations not available for key:', key);
      return key;
    }
    
    try {
      const keys = key.split('.');
      let value: any = translations[language];
      
      // If language doesn't exist in translations, fallback to 'ro'
      if (!value && language !== 'ro') {
        value = translations['ro'];
      }
      
      for (const k of keys) {
        if (value === null || value === undefined) {
          // Try fallback to Romanian if current language doesn't have the key
          if (language !== 'ro' && translations['ro']) {
            let fallbackValue: any = translations['ro'];
            for (const fallbackKey of keys) {
              if (fallbackValue === null || fallbackValue === undefined) {
                return key;
              }
              fallbackValue = fallbackValue[fallbackKey];
            }
            if (fallbackValue !== undefined && fallbackValue !== null) {
              return fallbackValue;
            }
          }
          return key;
        }
        value = value[k];
      }
      
      // Return the value as-is (could be string, array, object, etc.)
      return value !== undefined && value !== null ? value : key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}; 
